import type { ConditionFieldRow } from "../../../../../api/types"
import {
  BINDING_FIELD_KEY,
  isBindingOf,
  readBindings,
} from "../../studio/conditionRows"
import { normalizeCas } from "./conditionRowMerge"

/**
 * `BINDING_FIELD_KEY`, `readBindings` and the row-address predicate moved to
 * `../../studio/conditionRows` (TUR-1211 W5-L3b, review round 3): `flowState.ts`
 * (which may not depend on `studio-v2/`) needs the SAME reviewer-binding fold
 * to stop the worklist's live verdict-basis gate from reading a narrower
 * article set than the review-mask's own `entryArticles`/`substanceEntries`
 * do. Re-exported here so every existing import site in this folder keeps
 * working unchanged. `withBinding` (the WRITE side) stays here: it is a
 * review-mask-only concern, `flowState.ts` only ever reads.
 */
export { BINDING_FIELD_KEY, readBindings }

/**
 * This statement's bindings for this substance become exactly
 * `articleNumbers`. An empty list removes them, which restores the server's
 * own binding, or another statement's, or the article-agnostic default,
 * rather than asserting the disclosure applies to nothing.
 *
 * Drops ONLY the rows addressed to this framework + statementId + normalised
 * CAS. Every other row passes through untouched, including a
 * `framework: null` `__connected__` row of the same CAS, and another
 * statement's or another framework's binding of the same CAS: two
 * statements binding the same CAS to different articles are now two
 * independent rows, not one shared one.
 */
export function withBinding(
  rows: readonly ConditionFieldRow[],
  framework: string,
  statementId: string,
  cas: string,
  articleNumbers: readonly string[],
): ConditionFieldRow[] {
  const key = normalizeCas(cas) as string
  const kept = rows.filter(
    (r) =>
      !(
        isBindingOf(r, framework, statementId) &&
        normalizeCas(r.substanceCas) === key
      ),
  )
  return [
    ...kept,
    ...articleNumbers.map((articleNumber) => ({
      articleNumber,
      substanceCas: key,
      framework,
      fieldKey: BINDING_FIELD_KEY,
      value: statementId,
    })),
  ]
}
